/**
 * Sidebar Collapse Manager
 *
 * Manages sidebar collapse/restore when the side panel is expanded/collapsed.
 * Must be rendered inside SidebarProvider to access sidebar context.
 */

import { useEffect, useRef } from 'react';
import { useSidebar } from '@loader/ui';

import { useSidePanelStore } from '@/features/workspace/stores';

export function SidebarCollapseManager() {
  const { open, setOpen } = useSidebar();
  const isExpanded = useSidePanelStore((state) => state.isExpanded);

  // Store sidebar state in a ref - captures current `open` value for use in effect
  const sidebarStateBeforeExpandRef = useRef<boolean | null>(null);
  // Track current open state in a ref so effect doesn't depend on `open`
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (isExpanded) {
      // Expanding: save current sidebar state (only if not already saved) and collapse
      // The null check prevents overwriting in React Strict Mode double-run
      if (sidebarStateBeforeExpandRef.current === null) {
        sidebarStateBeforeExpandRef.current = openRef.current;
      }
      setOpen(false);
    } else if (sidebarStateBeforeExpandRef.current !== null) {
      // Collapsing: restore sidebar to saved state
      setOpen(sidebarStateBeforeExpandRef.current);
      sidebarStateBeforeExpandRef.current = null;
    }
  }, [isExpanded, setOpen]);

  return null;
}
